import { defineStore } from 'pinia';
import FarmAPI from '@/store/api/farm';
import { SelectedFarmBlock } from '@/store/models/common';
import { Farm, FarmManager, FarmSensor } from '@/store/models/farm';
import { QTablePagination } from '@/store/models/meta';
import { useCommonStore } from '@/store/modules/common';
import { useConfigurationStore } from '@/store/modules/configuration';
import { Coordinate } from 'ol/coordinate';
import { geoJSONFormat } from '@/utils/ol-map';
import { LocalStorage } from 'quasar';
import { buildQuery } from '@/utils/query';

/** `farm` as the selected farm: its point and envelope. Null when it has no point. */
export function farmSelection(farm: Farm): SelectedFarmBlock | null {
  if (!farm.pointGeom || typeof farm.pointGeom == 'string') return null;
  return {
    farmId: farm.id,
    name: farm.name,
    center: farm.pointGeom.coordinates as Coordinate,
    extent: farm.envelope ? geoJSONFormat.readGeometry(farm.envelope).getExtent() : null
  };
}

export const useFarmStore = defineStore('farm', {
  state: () => ({
    Farms: [] as Farm[],
    emptyFarm: {
      manager: null,
      owner: null,
      managerList: [] as number[]
    } as Farm,
    AddEditFarm: {
      manager: null,
      owner: null,
      managerList: [] as number[]
    } as Farm,
    FarmSensors: [] as FarmSensor[],
    emptyFarmSensor: {} as FarmSensor,
    AddEditFarmSensor: {} as FarmSensor,
    FetchingDefaultFarm: true,
    FarmTablePagination: {
      sortBy: 'id',
      descending: true,
      page: 1,
      rowsPerPage: 10,
      rowsNumber: 1
    } as QTablePagination
  }),
  actions: {
    setAddEditFarm(farm: Farm) {
      this.AddEditFarm = farm;
    },
    setFarmTablePagination(data: QTablePagination) {
      this.FarmTablePagination = data;
    },
    async getAllMyFarms(params?: { farm?: number }) {
      const query = buildQuery({ id: params?.farm });
      const farms: Farm[] = await FarmAPI.getAllMyFarms(query);
      for (const farm of farms) {
        farm.managerList = (farm.farmManager as FarmManager[])
          .filter(x => x.isActive)
          .map(x => x.manager);
      }
      this.Farms = farms;
    },
    async getMyDefaultFarm() {
      const commonStore = useCommonStore();
      const configurationStore = useConfigurationStore();
      const selectedFarmBlock = LocalStorage.getItem(
        'SelectedFarmBlock'
      ) as SelectedFarmBlock | null;
      let farmId = 0;
      if (selectedFarmBlock) {
        await commonStore.setSelectedFarmBlock(selectedFarmBlock);
        farmId = selectedFarmBlock.farmId;
      } else {
        const farm: Farm = await FarmAPI.getMyDefaultFarm();
        farmId = farm.id;
        const selection = farmSelection(farm);
        if (selection) commonStore.setSelectedFarmBlock(selection);
      }
      if (farmId) {
        await configurationStore.getLatestSeasonDateByFarm(farmId);
      }
      this.FetchingDefaultFarm = false;
    },
    setFetchingDefaultFarm(value: boolean) {
      this.FetchingDefaultFarm = value;
    },
    async getFarmById(id: number) {
      const farm = await FarmAPI.getFarmById(id);
      farm.managerList = (farm.farmManager as FarmManager[])
        .filter(x => x.isActive)
        .map(x => x.manager);
      this.AddEditFarm = farm;
    },
    async createFarm(farm: Farm) {
      const addedFarm = await FarmAPI.createFarm(farm);
      this.Farms.push(addedFarm);
      this.AddEditFarm = addedFarm;
    },
    async updateFarm(farm: Farm) {
      const updatedFarm = await FarmAPI.updateFarm(farm);
      const idx = this.Farms.findIndex(x => x.id == farm.id);
      if (idx !== -1) {
        this.Farms.splice(idx, 1, updatedFarm);
      }
      this.AddEditFarm = updatedFarm;
    },
    async deleteFarm(id: number) {
      await FarmAPI.deleteFarm(id);
      const idx = this.Farms.findIndex(x => x.id == id);
      if (idx !== -1) {
        this.Farms.splice(idx, 1);
      }
      this.AddEditFarm = JSON.parse(JSON.stringify(this.emptyFarm));
    },
    setAddEditFarmSensor(farmSensor: FarmSensor) {
      this.AddEditFarmSensor = farmSensor;
    },
    async getAllFarmSensor(params?: { farm?: number }) {
      const query = buildQuery({ farm: params?.farm });
      this.FarmSensors = await FarmAPI.getAllFarmSensor(query);
    },
    async createFarmSensor(farmSensor: FarmSensor) {
      const addedFarmSensor = await FarmAPI.createFarmSensor(farmSensor);
      this.FarmSensors.push(addedFarmSensor);
      this.AddEditFarm = addedFarmSensor as unknown as Farm;
    }
  }
});

